import {
  appAbilityAllowsModuleForBranch,
  appAbilityAllowsModuleForDepartment,
  appAbilityAllowsModuleUnscoped,
  appAbilityAllowsBranchAdministration,
  appAbilityAllowsWorkspaceSettings,
  appAbilityAllowsWorkspaceSettingsManagement,
} from "@/features/auth/casl/create-app-ability";
import { tenantAuthorizationActionDefinition } from "@ibada/shared-types/authorization";
import {
  BranchAdministration,
  BranchesManagement,
  RolesManagement,
  SubscriptionManagement,
  WorkspaceSettingsManagement,
} from "@/features/auth/casl/types";
import {
  getActiveUserBranchAccesses,
  hasWorkspaceAuthorityAccess,
  isSingleBranchWorkspaceAccess,
  resolveBranchIdForWorkspaceUserAccess,
  resolveWorkspaceContextBranchId,
} from "@/features/auth/lib/workspace-user-access";
import {
  TenantAuthorizationAction,
  TenantAuthorizationContext,
  TenantAuthorizationDecisionStatus,
  TenantAuthorizationDenialReason,
  TenantAuthorizationRouteGuardState,
  TenantFeatureEntitlementStatus,
  type ResolvedTenantAuthorizationContext,
  type TenantAuthorizationDecision,
  type TenantAuthorizationInput,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types";
import { WorkspaceAccessModule, WorkspaceAccessPermission } from "@/features/auth/model/auth-types";

// Compatibility exports. Canonical authorization domain definitions live beside this registry in `../model`.
export {
  TenantAuthorizationAction,
  TenantAuthorizationContext,
  TenantAuthorizationDecisionStatus,
  TenantAuthorizationDenialReason,
  TenantAuthorizationRouteGuardState,
  TenantFeatureEntitlementStatus,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types";
export type {
  ResolvedTenantAuthorizationContext,
  TenantAuthorizationDecision,
  TenantAuthorizationInput,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types";

type ActionRequirement = {
  /** False for shared actions that are declared but not yet adopted by this compatibility resolver. */
  implemented?: boolean;
  /** Every operation must declare its eligible contexts; omitted actions are denied by the resolver. */
  allowedContexts: readonly TenantAuthorizationContext[];
  module?: WorkspaceAccessModule;
  permission?: WorkspaceAccessPermission;
  /** Future plan/capability key; authorization continues to ignore it until product entitlement work exists. */
  featureEntitlementKey?: string;
  organizationSubject?: typeof BranchesManagement | typeof RolesManagement | typeof SubscriptionManagement;
  /** Organization-governance subjects are not modules: declare whether this operation only reads or mutates. */
  organizationAccess?: "read" | "update";
  requiredRolePermission?: string;
  requiresBranchAdministration?: boolean;
  /** Reads a branch identified by a URL resource ID; ownership or an active assignment to that exact branch is required. */
  requiresBranchAssignment?: boolean;
  /** Lets a workspace-level branches manager inspect every branch in that workspace. */
  allowsBranchesManagement?: boolean;
  requiresWorkspaceSettingsManagement?: boolean;
  /** Lets the mono-workspace settings entry be opened by its dedicated branch-scoped authority. */
  allowsMonoWorkspaceSettingsManagement?: boolean;
};

const VIEW = WorkspaceAccessPermission.VIEW;
const EDIT = WorkspaceAccessPermission.EDIT;
const BRANCH_CONTEXTS = [
  TenantAuthorizationContext.MonoBranch,
  TenantAuthorizationContext.MultiBranchBranch,
] as const;
const PARISH_CONTEXTS = [
  ...BRANCH_CONTEXTS,
  TenantAuthorizationContext.MultiBranchParish,
] as const;
const MODULE_ACTIONS: Readonly<
  Record<WorkspaceAccessModule, readonly [TenantAuthorizationAction, TenantAuthorizationAction]>
> = {
  [WorkspaceAccessModule.MEMBERS]: [TenantAuthorizationAction.MembersView, TenantAuthorizationAction.MembersEdit],
  [WorkspaceAccessModule.FINANCE]: [TenantAuthorizationAction.FinanceView, TenantAuthorizationAction.TransactionCreate],
  [WorkspaceAccessModule.BUDGETING]: [TenantAuthorizationAction.BudgetingView, TenantAuthorizationAction.BudgetingEdit],
  [WorkspaceAccessModule.ACTIVITIES]: [
    TenantAuthorizationAction.ActivitiesView,
    TenantAuthorizationAction.ActivitiesEdit,
  ],
  [WorkspaceAccessModule.REPORTS]: [TenantAuthorizationAction.ReportsView, TenantAuthorizationAction.ReportsEdit],
  [WorkspaceAccessModule.SETTINGS]: [TenantAuthorizationAction.SettingsView, TenantAuthorizationAction.SettingsEdit],
  [WorkspaceAccessModule.ASSETS]: [TenantAuthorizationAction.AssetsView, TenantAuthorizationAction.AssetsEdit],
  [WorkspaceAccessModule.GROUPS]: [TenantAuthorizationAction.GroupsView, TenantAuthorizationAction.GroupsEdit],
  [WorkspaceAccessModule.MINISTRIES]: [
    TenantAuthorizationAction.MinistriesView,
    TenantAuthorizationAction.MinistriesEdit,
  ],
  [WorkspaceAccessModule.DEPARTMENTS]: [
    TenantAuthorizationAction.DepartmentsView,
    TenantAuthorizationAction.DepartmentsEdit,
  ],
  [WorkspaceAccessModule.FORMS]: [TenantAuthorizationAction.FormsView, TenantAuthorizationAction.FormsEdit],
};

const ACTION_REQUIREMENTS: Readonly<Partial<Record<TenantAuthorizationAction, ActionRequirement>>> = {
  [TenantAuthorizationAction.DashboardView]: { allowedContexts: PARISH_CONTEXTS },
  // Parish may read aggregate members; ordinary member editing remains branch-only.
  [TenantAuthorizationAction.MembersView]: { module: WorkspaceAccessModule.MEMBERS, permission: VIEW, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.MembersEdit]: { module: WorkspaceAccessModule.MEMBERS, permission: EDIT, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.FinanceView]: { module: WorkspaceAccessModule.FINANCE, permission: VIEW, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.TransactionCreate]: { module: WorkspaceAccessModule.FINANCE, permission: EDIT, allowedContexts: PARISH_CONTEXTS },
  // Budgets support both concrete branch scope and parish/system-branch scope. The resolved branch still has
  // to carry the matching role grant; allowing the context does not turn branch visibility into permission.
  [TenantAuthorizationAction.BudgetingView]: { module: WorkspaceAccessModule.BUDGETING, permission: VIEW, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.BudgetingEdit]: { module: WorkspaceAccessModule.BUDGETING, permission: EDIT, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.BudgetApprove]: {
    module: WorkspaceAccessModule.BUDGETING,
    permission: VIEW,
    requiredRolePermission: "budgets.approve",
    allowedContexts: PARISH_CONTEXTS,
  },
  [TenantAuthorizationAction.ActivitiesView]: { module: WorkspaceAccessModule.ACTIVITIES, permission: VIEW, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.ActivitiesEdit]: { module: WorkspaceAccessModule.ACTIVITIES, permission: EDIT, allowedContexts: PARISH_CONTEXTS },
  // Reports remain branch-local. Organization configuration is available in parish/workspace context when the
  // caller holds the appropriate SETTINGS grant (for example, base currency during multi-branch setup).
  [TenantAuthorizationAction.ReportsView]: { module: WorkspaceAccessModule.REPORTS, permission: VIEW, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.ReportsEdit]: { module: WorkspaceAccessModule.REPORTS, permission: EDIT, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.AssetsView]: { module: WorkspaceAccessModule.ASSETS, permission: VIEW, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.AssetsEdit]: { module: WorkspaceAccessModule.ASSETS, permission: EDIT, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.GroupsView]: { module: WorkspaceAccessModule.GROUPS, permission: VIEW, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.GroupsEdit]: { module: WorkspaceAccessModule.GROUPS, permission: EDIT, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.MinistriesView]: { module: WorkspaceAccessModule.MINISTRIES, permission: VIEW, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.MinistriesEdit]: { module: WorkspaceAccessModule.MINISTRIES, permission: EDIT, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.FormsView]: { module: WorkspaceAccessModule.FORMS, permission: VIEW, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.FormsEdit]: { module: WorkspaceAccessModule.FORMS, permission: EDIT, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.DepartmentsView]: { module: WorkspaceAccessModule.DEPARTMENTS, permission: VIEW, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.DepartmentsEdit]: { module: WorkspaceAccessModule.DEPARTMENTS, permission: EDIT, allowedContexts: BRANCH_CONTEXTS },
  [TenantAuthorizationAction.SettingsView]: {
    module: WorkspaceAccessModule.SETTINGS,
    permission: VIEW,
    allowsMonoWorkspaceSettingsManagement: true,
    allowedContexts: PARISH_CONTEXTS,
  },
  [TenantAuthorizationAction.SettingsEdit]: {
    module: WorkspaceAccessModule.SETTINGS,
    permission: EDIT,
    allowsMonoWorkspaceSettingsManagement: true,
    allowedContexts: PARISH_CONTEXTS,
  },
  // Mono workspaces have one operational unit. Its settings are a distinct, branch-scoped role permission;
  // neither `users.manage` nor organization `branches.manage` can satisfy this requirement.
  [TenantAuthorizationAction.WorkspaceSettingsView]: {
    requiresWorkspaceSettingsManagement: true,
    allowedContexts: [TenantAuthorizationContext.MonoBranch],
  },
  [TenantAuthorizationAction.WorkspaceSettingsEdit]: {
    requiresWorkspaceSettingsManagement: true,
    allowedContexts: [TenantAuthorizationContext.MonoBranch],
  },
  // Audit access is workspace governance. It is available in every rendered tenant context only when the
  // user has the same Settings authority that the API enforces.
  [TenantAuthorizationAction.AuditLogView]: { module: WorkspaceAccessModule.SETTINGS, permission: VIEW, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.BranchesManage]: {
    organizationSubject: BranchesManagement,
    organizationAccess: "update",
    allowedContexts: PARISH_CONTEXTS,
  },
  // The Branch Details console is reached from organization branch management. A user with the organization
  // `branches.manage` permission may inspect every branch; branch-assigned users remain limited to their
  // exact branch below. This does not grant settings mutations by itself.
  [TenantAuthorizationAction.BranchInspect]: {
    requiresBranchAssignment: true,
    allowsBranchesManagement: true,
    allowedContexts: BRANCH_CONTEXTS,
  },
  [TenantAuthorizationAction.RolesManage]: {
    organizationSubject: RolesManagement,
    organizationAccess: "update",
    allowedContexts: PARISH_CONTEXTS,
  },
  [TenantAuthorizationAction.SubscriptionView]: { organizationSubject: SubscriptionManagement, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.SubscriptionManage]: {
    organizationSubject: SubscriptionManagement,
    organizationAccess: "update",
    // Reserved for a future workspace plan/entitlement resolver. No resolver is supplied today, so this does
    // not change current subscription-management access.
    featureEntitlementKey: "subscription-management",
    allowedContexts: PARISH_CONTEXTS,
  },
  [TenantAuthorizationAction.UsersView]: { requiresBranchAdministration: true, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.UsersEdit]: { requiresBranchAdministration: true, allowedContexts: PARISH_CONTEXTS },
  [TenantAuthorizationAction.BranchTeamView]: {
    requiresBranchAdministration: true,
    allowsBranchesManagement: true,
    allowedContexts: BRANCH_CONTEXTS,
  },
  [TenantAuthorizationAction.BranchTeamManage]: {
    requiresBranchAdministration: true,
    allowsBranchesManagement: true,
    allowedContexts: BRANCH_CONTEXTS,
  },
};

function resolveContext(
  access: TenantAuthorizationInput["access"],
  requestedBranchSlug?: string | null,
): ResolvedTenantAuthorizationContext {
  if (!access) return { kind: TenantAuthorizationContext.MultiBranchBranch, branchId: null };
  if (isSingleBranchWorkspaceAccess(access)) {
    return { kind: TenantAuthorizationContext.MonoBranch, branchId: resolveWorkspaceContextBranchId(access) };
  }
  if (!requestedBranchSlug || requestedBranchSlug === "parish") {
    return {
      kind: TenantAuthorizationContext.MultiBranchParish,
      branchId: access.workspace.systemBranch?.id ?? resolveWorkspaceContextBranchId(access),
    };
  }
  return {
    kind: TenantAuthorizationContext.MultiBranchBranch,
    branchId: resolveBranchIdForWorkspaceUserAccess(access, requestedBranchSlug),
  };
}

function hasRequiredRolePermission(
  access: NonNullable<TenantAuthorizationInput["access"]>,
  context: ResolvedTenantAuthorizationContext,
  permission: string,
): boolean {
  if (hasWorkspaceAuthorityAccess(access)) return true;
  const accepted = new Set([permission, permission.replace(".approve", ".manage")]);
  return access.roles.some((role) => {
    if (!role.permissions?.some((value) => accepted.has(value))) return false;
    if (context.kind === TenantAuthorizationContext.MultiBranchParish) return role.branchId === null;
    return role.branchId === null || role.branchId === context.branchId;
  });
}

export function getTenantAuthorizationActionRequirement(action: TenantAuthorizationAction): ActionRequirement | undefined {
  const implemented = ACTION_REQUIREMENTS[action];
  if (implemented) return implemented;
  const shared = tenantAuthorizationActionDefinition(action);
  if (!shared) return undefined;
  return { allowedContexts: shared.allowedContexts, implemented: false };
}

/** Adapter for existing module-level consumers while they migrate to named operational actions. */
export function resolveTenantAuthorizationModuleAction(
  module: WorkspaceAccessModule,
  permission: WorkspaceAccessPermission,
): TenantAuthorizationAction {
  const actions = MODULE_ACTIONS[module];
  // Fail closed. A module introduced without an explicit typed action must never inherit dashboard access.
  // `resolveTenantAuthorizationDecision` has no requirement for this sentinel and returns UNKNOWN_ACTION.
  return actions?.[permission === WorkspaceAccessPermission.EDIT ? 1 : 0] ?? "__UNKNOWN_TENANT_AUTHORIZATION_ACTION__" as TenantAuthorizationAction;
}

/**
 * Pure decision engine used by the hook, navigation filters, and route guards. It is deliberately
 * presentation-free so every consumer gets the same context and CASL evaluation.
 */
type FeatureEntitlement = TenantAuthorizationDecision["featureEntitlement"];

function featureEntitlementFor(requirement: ActionRequirement): FeatureEntitlement {
  return requirement.featureEntitlementKey
    ? { status: TenantFeatureEntitlementStatus.NotConfigured, key: requirement.featureEntitlementKey }
    : { status: TenantFeatureEntitlementStatus.NotConfigured };
}

function allow(
  action: TenantAuthorizationAction,
  context: ResolvedTenantAuthorizationContext,
  featureEntitlement: FeatureEntitlement,
): TenantAuthorizationDecision {
  return { status: TenantAuthorizationDecisionStatus.Allowed, context, action, featureEntitlement };
}

function deny(
  action: TenantAuthorizationAction,
  context: ResolvedTenantAuthorizationContext,
  featureEntitlement: FeatureEntitlement,
  denialReason: TenantAuthorizationDenialReason,
): TenantAuthorizationDecision {
  return { status: TenantAuthorizationDecisionStatus.Denied, context, action, denialReason, featureEntitlement };
}

function resolveEntitlementDecision(
  input: TenantAuthorizationInput,
  action: TenantAuthorizationAction,
  context: ResolvedTenantAuthorizationContext,
  requirement: ActionRequirement,
): TenantAuthorizationDecision | null {
  if (!requirement.featureEntitlementKey || input.isFeatureEntitled?.(requirement.featureEntitlementKey) !== false) {
    return null;
  }
  return {
    status: TenantAuthorizationDecisionStatus.EntitlementMissing,
    context,
    action,
    denialReason: TenantAuthorizationDenialReason.EntitlementMissing,
    featureEntitlement: { status: TenantFeatureEntitlementStatus.Missing, key: requirement.featureEntitlementKey },
  };
}

function resolveSpecialRequirement(
  input: TenantAuthorizationInput,
  action: TenantAuthorizationAction,
  context: ResolvedTenantAuthorizationContext,
  requirement: ActionRequirement,
  featureEntitlement: FeatureEntitlement,
): TenantAuthorizationDecision | null {
  if (requirement.module && requirement.permission) return null;
  if (requirement.requiresBranchAssignment) {
    if (requirement.allowsBranchesManagement && input.ability.can("update", BranchesManagement as never)) {
      return allow(action, context, featureEntitlement);
    }
    const hasAssignment = Boolean(context.branchId) && (
      hasWorkspaceAuthorityAccess(input.access!) ||
      getActiveUserBranchAccesses(input.access!).some((row) => row.branchId === context.branchId)
    );
    return hasAssignment
      ? allow(action, context, featureEntitlement)
      : deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingBranchAssignment);
  }
  if (requirement.requiresBranchAdministration) {
    // `BranchAdministration` is the historical CASL subject for the explicit `users.*`
    // permission module. Team/users surfaces now resolve through the same semantic
    // users decisions the API enforces; the legacy BranchTeam actions remain aliases.
    const requiredAccess = (
      action === TenantAuthorizationAction.BranchTeamManage ||
      action === TenantAuthorizationAction.UsersEdit
    )
      ? WorkspaceAccessPermission.EDIT
      : WorkspaceAccessPermission.VIEW;
    const monoWorkspaceGrant =
      context.kind === TenantAuthorizationContext.MonoBranch
      && (requiredAccess === WorkspaceAccessPermission.EDIT
        ? input.ability.can("update", BranchAdministration as never)
        : input.ability.can("read", BranchAdministration as never));
    if (monoWorkspaceGrant) {
      return allow(action, context, featureEntitlement);
    }
    if (requirement.allowsBranchesManagement && input.ability.can("update", BranchesManagement as never)) {
      return allow(action, context, featureEntitlement);
    }
    return appAbilityAllowsBranchAdministration(input.ability, context.branchId, requiredAccess)
      ? allow(action, context, featureEntitlement)
      : deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission);
  }
  if (requirement.requiresWorkspaceSettingsManagement) {
    const requiredAccess = action === TenantAuthorizationAction.WorkspaceSettingsEdit
      ? WorkspaceAccessPermission.EDIT
      : WorkspaceAccessPermission.VIEW;
    const monoWorkspaceGrant =
      context.kind === TenantAuthorizationContext.MonoBranch
      && (requiredAccess === WorkspaceAccessPermission.EDIT
        ? input.ability.can("update", WorkspaceSettingsManagement as never)
        : input.ability.can("read", WorkspaceSettingsManagement as never));
    if (monoWorkspaceGrant) {
      return allow(action, context, featureEntitlement);
    }
    return appAbilityAllowsWorkspaceSettingsManagement(input.ability, context.branchId, requiredAccess)
      ? allow(action, context, featureEntitlement)
      : deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission);
  }
  if (requirement.organizationSubject) {
    const abilityAction = requirement.organizationAccess ?? "read";
    return input.ability.can(abilityAction, requirement.organizationSubject)
      ? allow(action, context, featureEntitlement)
      : deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission);
  }
  return allow(action, context, featureEntitlement);
}

function resolveModuleRequirement(
  input: TenantAuthorizationInput,
  action: TenantAuthorizationAction,
  context: ResolvedTenantAuthorizationContext,
  requirement: Required<Pick<ActionRequirement, "module" | "permission">> & ActionRequirement,
  featureEntitlement: FeatureEntitlement,
): TenantAuthorizationDecision {
  if (requirement.module === WorkspaceAccessModule.SETTINGS) {
    const monoWorkspaceSettingsAllows =
      requirement.allowsMonoWorkspaceSettingsManagement &&
      context.kind === TenantAuthorizationContext.MonoBranch &&
      appAbilityAllowsWorkspaceSettingsManagement(input.ability, context.branchId, requirement.permission);
    return appAbilityAllowsWorkspaceSettings(input.ability, requirement.permission) || monoWorkspaceSettingsAllows
      ? allow(action, context, featureEntitlement)
      : deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission);
  }
  if (requirement.requiredRolePermission) {
    return hasRequiredRolePermission(input.access!, context, requirement.requiredRolePermission)
      ? allow(action, context, featureEntitlement)
      : deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission);
  }
  // Parish uses the visible system branch only as context. Effective workspace/organization permission is the
  // authority; a system-branch grant cannot independently expose parish navigation or content.
  if (context.kind === TenantAuthorizationContext.MultiBranchParish) {
    return appAbilityAllowsModuleUnscoped(input.ability, requirement.module, requirement.permission)
      ? allow(action, context, featureEntitlement)
      : deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission);
  }
  if (!context.branchId) {
    return deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingBranchContext);
  }
  const hasAssignment =
    hasWorkspaceAuthorityAccess(input.access!) ||
    getActiveUserBranchAccesses(input.access!).some((row) => row.branchId === context.branchId);
  if (!hasAssignment) {
    return deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingBranchAssignment);
  }
  if (appAbilityAllowsModuleForBranch(input.ability, requirement.module, requirement.permission, context.branchId)) {
    return allow(action, context, featureEntitlement);
  }
  /*
   * Departments surface only. A department grant is stored as a CASL rule conditioned on BOTH branchId and
   * departmentId, so a branch-only check can never match it — which left a department-only user denied on the
   * very pages ADR-0027 makes their landing, with the guard rendering nothing at all.
   *
   * Deliberately restricted to DEPARTMENTS: widening this to every module would let a department finance
   * manager satisfy a branch-level Finance gate and read branch-owned books. The departments list filters to
   * the user's own departments, each department page re-checks authority for that exact department, and the
   * backend remains the decision boundary for both.
   */
  if (
    requirement.module === WorkspaceAccessModule.DEPARTMENTS &&
    allowsAnyAssignedDepartment(
      input.access!, input.ability, requirement.module, requirement.permission, context.branchId,
    )
  ) {
    return allow(action, context, featureEntitlement);
  }
  return deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission);
}

/** True when any department the user holds in this branch satisfies the module permission. */
function allowsAnyAssignedDepartment(
  access: NonNullable<TenantAuthorizationInput["access"]>,
  ability: TenantAuthorizationInput["ability"],
  module: WorkspaceAccessModule,
  permission: WorkspaceAccessPermission,
  branchId: string,
): boolean {
  const departments = access.departmentAccess;
  // Absent for any workspace user who predates departments, so this is a real branch rather than a formality.
  if (!departments) return false;
  /*
   * No `department.branchId === branchId` filter: appAbilityAllowsModuleForDepartment is handed the CONTEXT
   * branch, and a department rule is conditioned on its own branch, so a grant from another branch already
   * fails to match. The explicit comparison could never change an outcome.
   */
  return departments.some((department) =>
    appAbilityAllowsModuleForDepartment(ability, module, permission, branchId, department.departmentId),
  );
}

function isContextAllowed(
  requirement: ActionRequirement,
  context: ResolvedTenantAuthorizationContext,
): boolean {
  return requirement.allowedContexts.includes(context.kind);
}

export function resolveTenantAuthorizationDecision(
  input: TenantAuthorizationInput,
  action: TenantAuthorizationAction,
): TenantAuthorizationDecision {
  const context = resolveContext(input.access, input.requestedBranchSlug);
  const requirement = getTenantAuthorizationActionRequirement(action);
  if (!requirement || requirement.implemented === false) {
    return deny(
      action,
      context,
      { status: TenantFeatureEntitlementStatus.NotConfigured },
      TenantAuthorizationDenialReason.UnknownAction,
    );
  }
  const featureEntitlement = featureEntitlementFor(requirement);

  if (input.isResolving) {
    return {
      status: TenantAuthorizationDecisionStatus.Resolving,
      context,
      action,
      denialReason: TenantAuthorizationDenialReason.AuthorizationStillResolving,
      featureEntitlement,
    };
  }
  if (!input.access) {
    return deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.NoActiveWorkspace);
  }
  if (!isContextAllowed(requirement, context)) {
    return deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission);
  }
  const entitlementDecision = resolveEntitlementDecision(input, action, context, requirement);
  if (entitlementDecision) return entitlementDecision;
  const specialDecision = resolveSpecialRequirement(input, action, context, requirement, featureEntitlement);
  if (specialDecision) return specialDecision;
  return resolveModuleRequirement(
    input,
    action,
    context,
    requirement as Required<Pick<ActionRequirement, "module" | "permission">> & ActionRequirement,
    featureEntitlement,
  );
}

/**
 * Department ownership is a resource scope layered on top of a branch. Keep that CASL detail here so
 * department pages do not become a second authorization system. Department access is never parish-wide.
 */
export function resolveTenantDepartmentAuthorizationDecision(
  input: TenantAuthorizationInput,
  module: WorkspaceAccessModule,
  permission: WorkspaceAccessPermission,
  branchId: string | null | undefined,
  departmentId: string | null | undefined,
): TenantAuthorizationDecision {
  const action = resolveTenantAuthorizationModuleAction(module, permission)
  const context = resolveContext(input.access, input.requestedBranchSlug)
  const requirement = getTenantAuthorizationActionRequirement(action)
  if (!requirement || requirement.implemented === false) {
    return deny(action, context, { status: TenantFeatureEntitlementStatus.NotConfigured }, TenantAuthorizationDenialReason.UnknownAction)
  }
  const featureEntitlement = featureEntitlementFor(requirement)
  if (input.isResolving) {
    return {
      status: TenantAuthorizationDecisionStatus.Resolving,
      context,
      action,
      denialReason: TenantAuthorizationDenialReason.AuthorizationStillResolving,
      featureEntitlement,
    }
  }
  if (!input.access) return deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.NoActiveWorkspace)
  if (context.kind === TenantAuthorizationContext.MultiBranchParish || !branchId || !departmentId) {
    return deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingBranchContext)
  }
  return appAbilityAllowsModuleForDepartment(input.ability, module, permission, branchId, departmentId)
    ? allow(action, context, featureEntitlement)
    : deny(action, context, featureEntitlement, TenantAuthorizationDenialReason.MissingPermission)
}

export function filterTenantAuthorizedItems<T>(
  items: readonly T[],
  input: TenantAuthorizationInput,
  actionForItem: (item: T) => TenantAuthorizationAction,
): T[] {
  return items.filter(
    (item) =>
      resolveTenantAuthorizationDecision(input, actionForItem(item)).status ===
      TenantAuthorizationDecisionStatus.Allowed,
  );
}

export function resolveTenantAuthorizationRouteGuardState(
  decision: TenantAuthorizationDecision | null,
): TenantAuthorizationRouteGuardState {
  if (!decision || decision.status === TenantAuthorizationDecisionStatus.Resolving) {
    return TenantAuthorizationRouteGuardState.Loading;
  }
  return decision.status === TenantAuthorizationDecisionStatus.Allowed
    ? TenantAuthorizationRouteGuardState.Allow
    : TenantAuthorizationRouteGuardState.Deny;
}
